import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import * as _ from 'underscore';

import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { AuthLevel, ServerService } from '../../../ts/services/ServerService';
import { ServiceLocator } from '../../ServiceLocator';
import { SiteUserSubspaceName } from '../../state/SpaceInfo';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { SiteUserSpaceProvider, SiteUserSpaceProviderProps } from './SiteUserSpaceProvider';

// The properties here must match the strings in the Routes' path below
export interface SiteUserSpaceRouteParams {
  readonly domain: string;
  readonly username: string;
}
export interface SiteUserRouteComponentProps extends RouteComponentProps<SiteUserSpaceRouteParams> {}

export interface SiteUserSpaceRouterOwnProps extends ReactDirectiveProps {
  readonly urls: SpacesSiteUrls;
  readonly provider: (subspaceName: SiteUserSubspaceName, providerProps: SiteUserSpaceComponentWithRouterProps) => (props: SiteUserRouteComponentProps) => JSX.Element;
}

export type SiteUserSpaceRouterProps = SiteUserSpaceRouterOwnProps;

export class SiteUserSpaceRouterComponent extends React.Component<SiteUserSpaceRouterProps> {
  private server: ServerService;

  public constructor(props: SiteUserSpaceRouterProps) {
    super(props);
    this.server = ServiceLocator.get<ServerService>('ServerService');
  }

  public render() {
    return (
      <Route path={decodeURIComponent(this.props.urls.userPath(':username', ':domain'))} render={({ match }) => (
        <Switch>
          <Route exact path={decodeURIComponent(this.props.urls.userWorkbooksPath(':username', ':domain'))} render={this.props.provider('workbooks', this.props)}/>
          <Route exact path={decodeURIComponent(this.props.urls.userProjectsPath(':username', ':domain'))} render={this.props.provider('projects', this.props)}/>
          <Route exact path={decodeURIComponent(this.props.urls.userViewsPath(':username', ':domain'))} render={this.props.provider('views', this.props)}/>
          {
            FeatureFlags.metricsSpaceEnabled &&
            <Route exact path={decodeURIComponent(this.props.urls.userMetricsPath(':username', ':domain'))} render={this.props.provider('metrics', this.props)}/>
          }
          <Route exact path={decodeURIComponent(this.props.urls.userDatasourcesPath(':username', ':domain'))} render={this.props.provider('datasources', this.props)}/>
          <Route exact path={decodeURIComponent(this.props.urls.userFlowsPath(':username', ':domain'))} render={this.props.provider('flows', this.props)}/>
          {this.protectedRouteForAlertsSubspace(this.server, match.params.username, match.params.domain)}
          {this.protectedRouteForSubscriptionsSubspace(this.server, match.params.username, match.params.domain)}
          {this.protectedRouteForSettingsSubspace(this.server, match.params.username, match.params.domain)}
          <Route exact path={decodeURIComponent(this.props.urls.userPath(':username', ':domain'))} render={({ match }) => (
            <Redirect to={decodeURIComponent(this.props.urls.userWorkbooksPath(match.params.username, match.params.domain))} />
          )} />
          <Route render={this.error}/>
        </Switch>
      )} />
    );
  }

  private error = (_props: RouteComponentProps): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  }

  private protectedRouteForAlertsSubspace(server: ServerService, username: string, domainName: string) {
    return server.isUserAuthorized(AuthLevel.SelfOrAdmin, username, domainName) &&
      <Route
        exact={true}
        path={decodeURIComponent(this.props.urls.userDataAlertsPath(':username', ':domain')) }
        render={this.props.provider('alerts', this.props)}
      />;
  }

  private protectedRouteForSettingsSubspace(server: ServerService, username: string, domainName: string) {
    return server.isUserAuthorized(AuthLevel.SelfOrAdmin, username, domainName) &&
      <Route
        exact={true}
        path={ decodeURIComponent(this.props.urls.userSettingsPath(':username', ':domain')) }
        render={this.props.provider('settings', this.props)}
      />;
  }

  private protectedRouteForSubscriptionsSubspace(server: ServerService, username: string, domainName: string) {
    return server.isUserAuthorized(AuthLevel.SelfOrAdmin, username, domainName) &&
      <Route
        exact={true}
        path={ decodeURIComponent(this.props.urls.userSubscriptionsPath(':username', ':domain')) }
        render={this.props.provider('subscriptions', this.props)}
      />;
  }
}

export type SiteUserSpaceComponentWithRouterProps = Omit<SiteUserSpaceRouterProps, 'provider'>;
const provider = (subspaceName: SiteUserSubspaceName, providerProps: SiteUserSpaceComponentWithRouterProps): (props: SiteUserRouteComponentProps) => JSX.Element => {
  return (props: SiteUserRouteComponentProps): JSX.Element => {
    const spaceProps: SiteUserSpaceProviderProps = {
      routeLocationSearch: props.location.search,
      routeLocationPath: props.location.pathname,
      userName: props.match.params.username,
      domainName: props.match.params.domain,
      subspaceName,
      urls: providerProps.urls,
      angularContext: providerProps.angularContext
    };
    return <SiteUserSpaceProvider {...spaceProps} />;
  };
};

type SiteUserSpaceComponentOwnProps = Omit<SiteUserSpaceRouterOwnProps, 'provider'>;
const SiteUserSpaceComponent = (props: SiteUserSpaceComponentWithRouterProps) => {
  return (
    <SiteUserSpaceRouterComponent {...props} provider={provider}/>
  );
};

export const SiteUserSpaceRouter: React.FunctionComponent<SiteUserSpaceComponentOwnProps> = SiteUserSpaceComponent;
